import { cmsNew } from "@/lib/cms/cms-new-i18n";

import type {
  ContainerHeight,
  ContainerHeightUnit,
  LayoutAlign,
  LayoutMaxWidth,
} from "./types";
import { ALIGN_CLASS, MAX_WIDTH_CLASS, cx } from "./internal";

/** Options UI partagées (Conteneur Contenu, Avancé → largeur max du contenu). */
export function getLayoutMaxWidthOptions(): Array<{
  label: string;
  value: LayoutMaxWidth;
}> {
  return [
    { label: cmsNew("cms.new.options.maxWidth.full", "Pleine largeur (100%)"), value: "full" },
    { label: cmsNew("cms.new.options.maxWidth.1024", "1024 px"), value: "1024" },
    { label: cmsNew("cms.new.options.maxWidth.1280", "1280 px"), value: "1280" },
    { label: cmsNew("cms.new.options.maxWidth.1320", "1320 px"), value: "1320" },
    { label: cmsNew("cms.new.options.maxWidth.1440", "1440 px"), value: "1440" },
    { label: cmsNew("cms.new.options.maxWidth.1536", "1536 px"), value: "1536" },
    { label: cmsNew("cms.new.options.maxWidth.prose", "Lecture (étroit ~768 px)"), value: "prose" },
  ];
}


/** Classes Tailwind de plafonnement (sans `w-full`). */
export function contentMaxWidthClass(
  value: LayoutMaxWidth | null | undefined,
): string {
  if (!value || value === "full") return "";
  return MAX_WIDTH_CLASS[value] ?? "";
}

/**
 * Shell interne sous le StyleWrapper : contraint le contenu tout en laissant le
 * wrapper (fond, bordures…) en pleine largeur. `null` si pas de plafond.
 */
export function blockContentShellClass(
  value: LayoutMaxWidth | null | undefined,
): string | null {
  const mw = contentMaxWidthClass(value);
  if (!mw) return null;
  return cx("w-full min-w-0", mw);
}

export const DEFAULT_CONTAINER_HEIGHT: ContainerHeight = {
  value: null,
  unit: "px",
};

export const CONTAINER_HEIGHT_UNIT_OPTIONS: ReadonlyArray<{
  label: string;
  value: ContainerHeightUnit;
}> = [
  { label: "px", value: "px" },
  { label: "vh", value: "vh" },
  { label: "vw", value: "vw" },
  { label: "dvh", value: "dvh" },
  { label: "vmin", value: "vmin" },
  { label: "vmax", value: "vmax" },
  { label: "rem", value: "rem" },
  { label: "em", value: "em" },
  { label: "%", value: "%" },
];

/** Style inline `height` du conteneur, ou `undefined` si valeur vide. */
export function resolveContainerHeightStyle(
  height?: ContainerHeight | null,
): { height: string } | undefined {
  const h = height ?? DEFAULT_CONTAINER_HEIGHT;
  if (
    h.value == null ||
    !Number.isFinite(h.value) ||
    h.value < 0
  ) {
    return undefined;
  }
  const unit = CONTAINER_HEIGHT_UNIT_OPTIONS.some((o) => o.value === h.unit)
    ? h.unit
    : "px";
  return { height: `${h.value}${unit}` };
};

/** Classes Tailwind `align-items` (flex ou grid). */
export function layoutAlignItemsClass(align: LayoutAlign): string {
  return ALIGN_CLASS[align];
}

/** Options UI pour l'alignement vertical (grid / flex transversal). */
export const LAYOUT_VERTICAL_ALIGN_OPTIONS: Array<[LayoutAlign, string]> = [
  ["start", "Haut"],
  ["center", "Centre"],
  ["end", "Bas"],
  ["stretch", "Étiré"],
];
